// @flow

import { graphql } from "gatsby";
import React from "react";
import Col from "react-bootstrap/cjs/Col";
import Row from "react-bootstrap/Row";

import {ContinueLink, InnovationAnimation, PageHead, VideoPlayer} from "@/components";
import type {InnovationFrame} from "@/components/InnovationAnimation/InnovationAnimation";
import {AppLayout} from "@/containers";

type FrameSource = {
    src: string,
};

type Frame = {
    large: FrameSource,
    largeLowQuality: FrameSource,
    sm: FrameSource,
    smLowQuality: FrameSource,
};

type Props = {
    data: {
        innovation: {
            mindsetTitle: string,
            mindsetText: string,
            valuesTitle: string,
            valuesText: string,
            virtualWorldTitle: string,
            virtualWorldText: string,
            partnersTitle: string,
            partnersText: string,
            frames: {
                childImageSharp: Frame,
            }[],
        },
    },
};

function transformFrames(
    frames: { childImageSharp: Frame }[],
): InnovationFrame[] {
    return frames.map(frame => ({
        large: {
            original: frame.childImageSharp.large.src,
            compressed: frame.childImageSharp.largeLowQuality.src,
        },
        sm: {
            original: frame.childImageSharp.sm.src,
            compressed: frame.childImageSharp.smLowQuality.src,
        },
    }));
}

const InnovationPage = ({ data }: Props): React$Element<typeof AppLayout> => {
    const {
        mindsetTitle,
        mindsetText,
        valuesTitle,
        valuesText,
        virtualWorldTitle,
        virtualWorldText,
        partnersTitle,
        partnersText,
        frames,
    } = data.innovation;

    const frameImages = transformFrames(frames);

    return (
        <AppLayout headerType="light">
            <section className="bru-fluid-container px-0 pt-0">
                <InnovationAnimation frames={frameImages} />
            </section>

            <section className="container">
                <Row>
                    <Col md={8} lg={7}>
                        <h1 className="sr-only">{mindsetTitle}</h1>

                        <p className="h3 text-muted pb-3">{mindsetText}</p>
                    </Col>
                </Row>
            </section>

            <section className="container">
                <Row className={"justify-content-end"}>
                    <Col md={10} lg={9}>
                        <h1 className="sr-only">{valuesTitle}</h1>

                        <p className="h1 text-primary-gradient">{valuesText}</p>
                    </Col>
                </Row>
            </section>

            <section className="bru-fluid-container altered-margin-sm">
                <VideoPlayer
                    playerId="jw-innovation-1-player"
                    playerScript="https://cdn.jwplayer.com/libraries/BtHMvLty.js"
                    file="https://content.jwplatform.com/videos/4p2GWxJz-ODZfK8Gq.mp4"
                    aspectRatio="21:9"
                    allowMultipleVideoPlayback={true}
                />
            </section>

            <section className="container">
                <Row className="justify-content-center">
                    <Col lg={10} xl={8}>
                        <Row>
                            <Col md={6}>
                                <h1 className="h3">{virtualWorldTitle}</h1>
                            </Col>
                            <Col md={6}>
                                <span className="text-muted">
                                    {virtualWorldText}
                                </span>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </section>

            <section className="bru-fluid-container altered-margin-sm">
                <VideoPlayer
                    playerId="jw-innovation-2-player"
                    playerScript="https://cdn.jwplayer.com/libraries/BtHMvLty.js"
                    file="https://content.jwplatform.com/videos/cWTUfRLG-Jo4KPliO.mp4"
                    aspectRatio="16:9"
                    controls={false}
                    allowMultipleVideoPlayback={true}
                />
            </section>

            <section className="container">
                <Row>
                    <Col md={{ span: 5, offset: 7 }}>
                        <h1 className="sr-only">{partnersTitle}</h1>

                        <div className="text-muted pb-4">{partnersText}</div>

                        <ContinueLink variant="dark" to="/contact">
                            {partnersTitle}
                        </ContinueLink>
                    </Col>
                </Row>
            </section>
        </AppLayout>
    );
};

export const Head = ({location}: HeadProps): React$Element<typeof PageHead> => (
    <PageHead
        title="Innovation"
        description="Our mission is to supply professionals with high-quality fabrics and set the industry standard for innovation that is valuable for our customers across the globe."
        location={location}
    />
);

export const query: Props = graphql`
    query InnovationPageQuery {
        innovation {
            mindsetTitle
            mindsetText
            valuesTitle
            valuesText
            virtualWorldTitle
            virtualWorldText
            partnersTitle
            partnersText
            frames {
                childImageSharp {
                    large: original {
                        src
                    }
                    largeLowQuality: resize(quality: 80) {
                        src
                    }
                    sm: resize(width: 1200, height: 675, quality: 100) {
                        src
                    }
                    smLowQuality: resize(
                        width: 1200
                        height: 675
                        quality: 80
                    ) {
                        src
                    }
                }
            }
        }
    }
`;

export default InnovationPage;
